<template>
    <div class="leave-wallchart">
        <!-- Toolbar -->
        <div class="wc-toolbar mb-4">
            <div class="d-flex align-center flex-wrap" style="gap: 10px;">
                <v-menu
                    v-model="filterMenu"
                    :close-on-content-click="false"
                    offset-y
                    min-width="300"
                    max-width="320"
                >
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn outlined small class="text-none wc-filter-btn" v-bind="attrs" v-on="on">
                            <v-icon small left>mdi-filter-variant</v-icon>
                            Filter
                            <span v-if="activeFilterCount" class="wc-filter-count">{{ activeFilterCount }}</span>
                        </v-btn>
                    </template>

                    <v-card class="wc-filter-card">
                        <div class="wc-filter-head">
                            <span class="wc-filter-title">Filters</span>
                            <v-btn text x-small color="primary" class="text-none" @click="clearFilters">
                                Clear filter
                            </v-btn>
                        </div>

                        <div class="wc-filter-body">
                            <div class="wc-filter-label">Sort by</div>
                            <v-radio-group v-model="sortBy" hide-details dense class="mt-0">
                                <v-radio label="First name" value="first" />
                                <v-radio label="Last name" value="last" />
                                <v-radio label="Department" value="department" />
                            </v-radio-group>

                            <div class="wc-filter-label mt-4">Account type</div>
                            <v-radio-group v-model="accountType" hide-details dense class="mt-0">
                                <v-radio label="All accounts" value="all" />
                                <v-radio label="Department managers" value="managers" />
                                <v-radio label="Users I approve" value="approve" />
                            </v-radio-group>

                            <div class="wc-filter-label mt-4">Departments</div>
                            <v-checkbox
                                :input-value="allDepartments"
                                label="All departments"
                                hide-details
                                dense
                                class="mt-0"
                                @change="setAllDepartments"
                            />
                            <div class="wc-dep-list">
                                <div v-for="dep in roleOptions" :key="dep.id" class="d-flex align-center wc-dep-row">
                                    <v-checkbox
                                        :input-value="isDepChecked(dep.id)"
                                        :label="dep.name"
                                        hide-details
                                        dense
                                        class="mt-0"
                                        @change="toggleDep(dep.id)"
                                    />
                                    <v-spacer />
                                    <span v-if="myTeamIdSet.has(dep.id)" class="grey--text text-caption">My Dept</span>
                                </div>
                            </div>
                        </div>
                    </v-card>
                </v-menu>

                <v-text-field
                    v-model="search"
                    placeholder="Search users…"
                    outlined
                    dense
                    hide-details
                    clearable
                    prepend-inner-icon="mdi-magnify"
                    class="wc-search"
                />

                <v-chip small label outlined class="wc-count">
                    <v-icon x-small left>mdi-account-multiple</v-icon>
                    {{ filteredUsers.length }} {{ filteredUsers.length === 1 ? 'user' : 'users' }}
                </v-chip>

                <v-spacer />

                <div class="d-flex align-center">
                    <v-btn icon small @click="prev">
                        <v-icon>mdi-chevron-left</v-icon>
                    </v-btn>

                    <v-menu v-model="rangeMenu" :close-on-content-click="false" offset-y left>
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn text small class="text-none font-weight-bold wc-range-btn" v-bind="attrs" v-on="on">
                                {{ rangeLabel }}
                                <v-icon x-small right>mdi-calendar-range</v-icon>
                            </v-btn>
                        </template>
                        <v-card class="pa-3" width="320">
                            <div class="text-caption grey--text mb-2">Custom date range (max 31 days)</div>
                            <v-date-picker
                                v-model="pendingRange"
                                range
                                no-title
                                full-width
                                color="primary"
                                first-day-of-week="1"
                            />
                            <div class="text-caption mt-2" :class="rangeSpanError ? 'error--text' : 'grey--text'">
                                <template v-if="pendingSorted.length === 2">
                                    {{ pendingSpan }} day{{ pendingSpan === 1 ? '' : 's' }} selected
                                    <span v-if="rangeSpanError"> — limit is 31</span>
                                </template>
                                <template v-else>Pick a start and end date</template>
                            </div>
                            <div class="d-flex justify-space-between align-center mt-3">
                                <v-btn text small class="text-none" @click="resetToMonth">Reset to month</v-btn>
                                <v-btn small color="primary" class="text-none" :disabled="!canApplyRange" @click="applyRange">
                                    Apply
                                </v-btn>
                            </div>
                        </v-card>
                    </v-menu>

                    <v-btn icon small @click="next">
                        <v-icon>mdi-chevron-right</v-icon>
                    </v-btn>
                    <v-btn outlined small class="ml-2 text-none" @click="goToday">Today</v-btn>
                </div>
            </div>
        </div>

        <LeaveWallchartGrid
            :days="days"
            :rows="displayRows"
            :legend-types="legendTypes"
            :color-for-type="colorForType"
            @row-click="openCalendar"
            @cell-click="openDetail"
        />

        <!-- User calendar dialog -->
        <v-dialog v-model="calendarDialog" max-width="1180" content-class="uc-dialog-shell">
            <UserLeaveCalendar
                v-if="calendarUser"
                :key="calendarUser.id"
                :user="calendarUser"
                :requests="requests"
                :balances="balances"
                :leave-types="leaveTypes"
                :initial-year="$date(monthAnchor).year()"
                @close="calendarDialog = false"
            />
        </v-dialog>

        <!-- Leave details -->
        <LeaveDetailModal
            :request="detailDialog ? selectedRequest : null"
            :leave-types="leaveTypes"
            :users="users"
            @close="detailDialog = false"
        />
    </div>
</template>

<script>
import UserLeaveCalendar from './UserLeaveCalendar.vue';
import LeaveDetailModal from './LeaveDetailModal.vue';
import LeaveWallchartGrid from './LeaveWallchartGrid.vue';
import {
    colorForType as resolveTypeColor,
    fullName as buildFullName,
    isApproved,
    buildDays,
    buildWallchartRows,
} from './leaveUtils';

export default {
    name: 'LeaveWallchart',
    components: { UserLeaveCalendar, LeaveDetailModal, LeaveWallchartGrid },
    props: {
        requests: { type: Array, default: () => [] },
        users: { type: Array, default: () => [] },
        leaveTypes: { type: Array, default: () => [] },
        balances: { type: Array, default: () => [] },
        roles: { type: Array, default: () => [] },
        // Array of team ids the current user manages, or null = unrestricted (Super Admin).
        managedTeamIds: { default: null },
        roleManagers: { type: Array, default: () => [] },
        currentUserId: { default: null },
    },
    data() {
        return {
            monthAnchor: this.$date().format('YYYY-MM-DD'), // first of month, replaced in created()
            customStart: null, // 'YYYY-MM-DD' — set = custom range mode instead of month
            customEnd: null,
            rangeMenu: false,
            pendingRange: [], // v-date-picker range model while the menu is open
            search: '',
            allDeptsChecked: true, // "All departments" toggle
            selectedRoles: [], // explicitly selected team ids (used only when allDeptsChecked is false)
            sortBy: 'first', // 'first' | 'last' | 'department'
            accountType: 'all', // 'all' | 'managers' | 'approve'
            filterMenu: false,
            detailDialog: false,
            selectedRequest: null,
            calendarDialog: false,
            calendarUser: null,
        };
    },
    created() {
        this.monthAnchor = this.$date().startOf('month').format('YYYY-MM-DD');
    },
    computed: {
        isCustomRange() {
            return !!(this.customStart && this.customEnd);
        },
        days() {
            let start;
            let count;
            if (this.isCustomRange) {
                start = this.$date(this.customStart);
                count = this.$date(this.customEnd).diff(start, 'day') + 1;
            } else {
                start = this.$date(this.monthAnchor).startOf('month');
                count = start.endOf('month').date();
            }
            count = Math.min(Math.max(count, 1), 31); // hard 31-day cap
            const todayKey = this.$date().format('YYYY-MM-DD');
            return buildDays(start.format('YYYY-MM-DD'), count, todayKey);
        },
        rangeLabel() {
            if (this.isCustomRange) {
                const s = this.$date(this.customStart);
                const e = this.$date(this.customEnd);
                const sameYear = s.year() === e.year();
                return `${s.format(sameYear ? 'D MMM' : 'D MMM YYYY')} – ${e.format('D MMM YYYY')}`;
            }
            return this.$date(this.monthAnchor).format('MMMM YYYY');
        },
        pendingSorted() {
            return (this.pendingRange || []).filter(Boolean).slice().sort();
        },
        pendingSpan() {
            const r = this.pendingSorted;
            if (r.length < 2) return 0;
            return this.$date(r[1]).diff(this.$date(r[0]), 'day') + 1;
        },
        rangeSpanError() {
            return this.pendingSpan > 31;
        },
        canApplyRange() {
            return this.pendingSorted.length === 2 && !this.rangeSpanError;
        },
        activeUsers() {
            return (this.users || [])
                .filter((u) => u && !u.deleted_at)
                .map((u) => ({ ...u, role_name: (u.role && u.role.name) || u.role_name }));
        },
        allDepartments() {
            return this.allDeptsChecked;
        },
        // Team ids the current user belongs to (for the "My Dept" badge).
        myTeamIdSet() {
            const me = (this.users || []).find((u) => u.id === this.currentUserId);
            const ids = new Set();
            if (me) {
                (me.role_members || []).forEach((m) => { if (m.user_role_id) ids.add(m.user_role_id); });
                if (me.role_id) ids.add(me.role_id);
            }
            return ids;
        },
        // User ids that are a manager of at least one team.
        managerUserIdSet() {
            return new Set((this.roleManagers || []).map((r) => r.manager_id));
        },
        // User ids the current user is the named approver for.
        approveUserIdSet() {
            const s = new Set();
            (this.requests || []).forEach((r) => {
                const approver = r.approver_id || (r.approver && r.approver.id);
                if (approver && approver === this.currentUserId) {
                    s.add(r.user_id || (r.user && r.user.id));
                }
            });
            return s;
        },
        // Count of constraints set inside the Filter popover (search lives in the
        // toolbar and shows its own state).
        activeFilterCount() {
            let n = 0;
            if (this.accountType !== 'all') n += 1;
            if (!this.allDeptsChecked) n += 1;
            if (this.sortBy !== 'first') n += 1;
            return n;
        },
        // Team/department options. Super Admin (managedTeamIds == null) sees the
        // full Manager's Hub roles list; a manager sees only the teams they
        // actually manage.
        roleOptions() {
            let base;
            if (this.roles && this.roles.length) {
                base = [...this.roles];
            } else {
                // Fallback: derive team names from the (scoped) users themselves
                const map = {};
                this.activeUsers.forEach((u) => {
                    (u.role_members || []).forEach((m) => {
                        if (m.user_role_id) map[m.user_role_id] = (m.role && m.role.name) || `Team ${m.user_role_id}`;
                    });
                    if (u.role_id && !map[u.role_id]) map[u.role_id] = u.role_name || `Team ${u.role_id}`;
                });
                base = Object.keys(map).map((id) => ({ id: Number(id), name: map[id] }));
            }

            if (Array.isArray(this.managedTeamIds)) {
                const allowed = new Set(this.managedTeamIds);
                base = base.filter((r) => allowed.has(r.id));
            }

            return base.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        },
        filteredUsers() {
            let list = this.activeUsers.slice();
            if (!this.allDeptsChecked) {
                // when "All departments" is off, show only explicitly-selected teams
                // (nothing selected -> no users)
                list = list.filter((u) => this.userInSelectedTeams(u));
            }
            if (this.accountType === 'managers') {
                list = list.filter((u) => this.managerUserIdSet.has(u.id));
            } else if (this.accountType === 'approve') {
                list = list.filter((u) => this.approveUserIdSet.has(u.id));
            }
            if (this.search) {
                const q = this.search.toLowerCase();
                list = list.filter((u) => this.fullName(u).toLowerCase().includes(q));
            }
            return list.sort(this.userSorter);
        },
        userSorter() {
            const by = this.sortBy;
            const name = (u) => this.fullName(u);
            return (a, b) => {
                if (by === 'department') {
                    const c = this.primaryTeamName(a).localeCompare(this.primaryTeamName(b));
                    return c || name(a).localeCompare(name(b));
                }
                if (by === 'last') {
                    const c = (a.last_name || name(a)).localeCompare(b.last_name || name(b));
                    return c || (a.first_name || '').localeCompare(b.first_name || '');
                }
                const c = (a.first_name || name(a)).localeCompare(b.first_name || name(b));
                return c || (a.last_name || '').localeCompare(b.last_name || '');
            };
        },
        approvedRequests() {
            return (this.requests || []).filter(isApproved);
        },
        displayRows() {
            return buildWallchartRows({
                users: this.filteredUsers,
                requests: this.requests,
                days: this.days,
                leaveTypes: this.leaveTypes,
                balances: this.balances,
                year: this.$date(this.monthAnchor).year(),
            });
        },
        legendTypes() {
            const ids = new Set();
            this.approvedRequests.forEach((r) => {
                const id = r.leave_type_id || (r.leave_type && r.leave_type.id);
                if (id) ids.add(id);
            });
            const known = (this.leaveTypes || []).filter((lt) => ids.has(lt.id));
            if (known.length) return known;
            const names = {};
            this.approvedRequests.forEach((r) => {
                const id = r.leave_type_id || (r.leave_type && r.leave_type.id);
                if (id && !names[id]) names[id] = (r.leave_type && r.leave_type.name) || 'Leave';
            });
            return Object.keys(names).map((id) => ({ id: Number(id), name: names[id] }));
        },
    },
    watch: {
        rangeMenu(open) {
            if (open) {
                this.pendingRange = this.isCustomRange
                    ? [this.customStart, this.customEnd]
                    : [this.days[0].key, this.days[this.days.length - 1].key];
            }
        },
        calendarDialog(open) {
            // v-dialog: push it below the sticky nav (which renders above dialogs)
            if (!open) return;
            this.$nextTick(() => {
                const dlg = document.querySelector('.v-dialog.uc-dialog-shell');
                if (!dlg) return;
                const nav = document.querySelector('.top-navigation');
                const top = nav ? nav.getBoundingClientRect().bottom : 120;
                dlg.style.marginTop = `${Math.max(24, Math.round(top) + 12)}px`;
            });
        },
    },
    methods: {
        shiftRange(dir) {
            const len = this.$date(this.customEnd).diff(this.$date(this.customStart), 'day') + 1;
            if (dir < 0) {
                this.customEnd = this.$date(this.customStart).subtract(1, 'day').format('YYYY-MM-DD');
                this.customStart = this.$date(this.customEnd).subtract(len - 1, 'day').format('YYYY-MM-DD');
            } else {
                this.customStart = this.$date(this.customEnd).add(1, 'day').format('YYYY-MM-DD');
                this.customEnd = this.$date(this.customStart).add(len - 1, 'day').format('YYYY-MM-DD');
            }
        },
        prev() {
            if (this.isCustomRange) { this.shiftRange(-1); return; }
            this.monthAnchor = this.$date(this.monthAnchor).subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
        },
        next() {
            if (this.isCustomRange) { this.shiftRange(1); return; }
            this.monthAnchor = this.$date(this.monthAnchor).add(1, 'month').startOf('month').format('YYYY-MM-DD');
        },
        goToday() {
            this.customStart = null;
            this.customEnd = null;
            this.pendingRange = [];
            this.monthAnchor = this.$date().startOf('month').format('YYYY-MM-DD');
        },
        applyRange() {
            if (!this.canApplyRange) return;
            [this.customStart, this.customEnd] = this.pendingSorted;
            this.rangeMenu = false;
        },
        resetToMonth() {
            this.customStart = null;
            this.customEnd = null;
            this.pendingRange = [];
            this.rangeMenu = false;
        },
        colorForType(id) {
            return resolveTypeColor(id, this.leaveTypes);
        },
        openCalendar(user) {
            this.calendarUser = user;
            this.calendarDialog = true;
        },
        // Match Manager's Hub: a user belongs to a team via role_members (multi-role),
        // falling back to the legacy single role_id.
        userInSelectedTeams(user) {
            const sel = this.selectedRoles;
            if (user.role_members && user.role_members.length) {
                return user.role_members.some((m) => sel.includes(m.user_role_id));
            }
            return sel.includes(user.role_id);
        },
        primaryTeamName(u) {
            if (u.role_members && u.role_members.length && u.role_members[0].role) {
                return u.role_members[0].role.name || 'Other';
            }
            return u.role_name || u.position_name || 'Other';
        },
        clearFilters() {
            this.search = '';
            this.sortBy = 'first';
            this.accountType = 'all';
            this.allDeptsChecked = true;
            this.selectedRoles = [];
        },
        setAllDepartments(checked) {
            // checking "All departments" → show everyone;
            // unchecking it → clears every individual team (no users)
            this.allDeptsChecked = checked !== false;
            this.selectedRoles = [];
        },
        isDepChecked(id) {
            return this.allDeptsChecked || this.selectedRoles.includes(id);
        },
        toggleDep(id) {
            const allIds = this.roleOptions.map((r) => r.id);
            let sel;
            if (this.allDeptsChecked) {
                sel = allIds.filter((x) => x !== id); // was "all" — drop this one
            } else if (this.selectedRoles.includes(id)) {
                sel = this.selectedRoles.filter((x) => x !== id);
            } else {
                sel = [...this.selectedRoles, id];
            }
            if (sel.length === allIds.length) {
                // everything selected → normalise back to "All departments"
                this.allDeptsChecked = true;
                this.selectedRoles = [];
            } else {
                this.allDeptsChecked = false;
                this.selectedRoles = sel;
            }
        },
        openDetail(request) {
            this.selectedRequest = request;
            this.detailDialog = true;
        },
        fullName(user) {
            return buildFullName(user);
        },
    },
};
</script>

<style lang="scss" scoped>
.wc-toolbar {
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 10px;
    padding: 10px 12px;
}

.wc-filter-btn {
    border-color: #d0d0d0;
    color: #455a64;
    letter-spacing: 0;
}

.wc-filter-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    margin-left: 6px;
    border-radius: 9px;
    background: #1976d2;
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    line-height: 1;
}

.wc-search {
    max-width: 260px;
}

.wc-count {
    color: #607d8b;
    font-weight: 600;
}

.wc-range-btn {
    min-width: 150px;
    letter-spacing: 0;
}

.wc-range-btn ::v-deep .v-btn__content {
    font-size: 0.95rem;
}

.wc-filter-card {
    display: flex;
    flex-direction: column;
    max-height: 60vh;
}

.wc-filter-head {
    flex: none;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 16px;
    border-bottom: 1px solid #eee;
}

.wc-filter-title {
    font-size: 15px;
    font-weight: 700;
    color: #263238;
}

.wc-filter-body {
    overflow-y: auto;
    padding: 12px 16px 16px;
}

.wc-dep-list {
    max-height: 190px;
    overflow-y: auto;
    margin-top: 2px;
}

.wc-filter-label {
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #90a4ae;
    margin-bottom: 2px;
}

.wc-filter-card ::v-deep .v-input--radio-group .v-radio {
    margin-bottom: 2px;
}

.wc-filter-card ::v-deep .v-label {
    font-size: 13px;
}

.wc-dep-row ::v-deep .v-input--selection-controls {
    margin-top: 0;
    padding-top: 4px;
}
</style>
